import {
  createOperationScopeSnapshot,
  type QueryGroup,
  showApiErrorNotification,
  useApiMutation,
  useQueryClient,
  useTeam,
} from '@vrplatform/data';
import { showWarnNotification } from '@vrplatform/theme';
import { useCallback, useId } from 'react';
import { useConnectionDetailDrawer } from './useConnectionDetailDrawer';
import { useTrackOperation } from '../operations';

export type ConnectionReconnectRequiredIssue = {
  type: 'reconnect-required';
  code: 'CONNECTION_RECONNECT_REQUIRED';
  message: string;
  connectionId: string;
  connectionName: string | null;
};

export const getConnectionReconnectRequiredIssue = (
  error: unknown
): ConnectionReconnectRequiredIssue | null => {
  // useApiMutation rejects with the plain API error envelope.
  if (
    typeof error !== 'object' ||
    error === null ||
    !('code' in error) ||
    error.code !== 'CONNECTION_RECONNECT_REQUIRED' ||
    !('message' in error) ||
    typeof error.message !== 'string' ||
    !('context' in error)
  ) {
    return null;
  }

  const { context } = error;

  if (
    typeof context !== 'object' ||
    context === null ||
    !('connectionId' in context) ||
    typeof context.connectionId !== 'string'
  ) {
    return null;
  }

  return {
    type: 'reconnect-required',
    code: 'CONNECTION_RECONNECT_REQUIRED',
    message: error.message,
    connectionId: context.connectionId,
    connectionName:
      'connectionName' in context && typeof context.connectionName === 'string'
        ? context.connectionName
        : null,
  };
};

export const useExtractMutation = () => {
  const [team] = useTeam();
  const queryClient = useQueryClient();
  const trackOperation = useTrackOperation();
  const { open: openConnectionDetailDrawer } = useConnectionDetailDrawer();
  const notifyId = useId();

  const { mutateAsync, isPending: loading } = useApiMutation(
    'post',
    '/connections/extract'
  );

  const extract = useCallback(
    async ({
      connectionId,
      params,
      extractor,
      forceUpdate,
      refreshQueryKeys,
      refreshApiGroups,
      openSync = true,
    }: {
      connectionId: string;
      extractor: string | undefined;
      openSync?: boolean;
      forceUpdate?: boolean;
      params: {
        uniqueRef?: string | null;
        date?: string | null;
      };
      refreshQueryKeys?: readonly string[];
      refreshApiGroups?: readonly QueryGroup[];
    }) => {
      try {
        const scope = createOperationScopeSnapshot({
          apiTarget: 'regional',
          dataRegion: team.dataRegion,
          regionalApiBaseUrl: team.apiBaseUrl,
          requesterTeamId: team.id,
        });
        const results = await mutateAsync({
          body: {
            connections: [
              {
                forceUpdate,
                connectionId,
                params,
                type: extractor,
              },
            ],
          },
        });

        const operationKey = trackOperation({
          acknowledgement: results.data,
          scope,
          effects: [
            {
              id: `connection-extract:${results.data.operationId}:api`,
              kind: 'api',
              target: { scope: 'team', teamIds: [team.id] },
              groups: ['connections', ...(refreshApiGroups ?? [])],
            },
            {
              id: `connection-extract:${results.data.operationId}:hasura`,
              kind: 'hasura',
              target: { scope: 'team', teamIds: [team.id] },
              queryKeys: ['connections', ...(refreshQueryKeys ?? [])],
            },
            {
              id: `${notifyId}:completed`,
              kind: 'notification',
              status: 'completed',
              title: 'Fetch complete',
              message: 'The latest connection data is ready.',
            },
            {
              id: `${notifyId}:failed`,
              kind: 'notification',
              status: 'failed',
              title: 'Fetch failed',
              message:
                'The connection could not finish fetching data. Open it to retry.',
            },
          ],
        });

        void queryClient.invalidateQueries({ queryKey: ['connections'] });
        for (const queryGroup of refreshApiGroups ?? []) {
          void queryClient.invalidateQueries({ queryKey: [queryGroup] });
        }

        if (openSync) {
          openConnectionDetailDrawer(connectionId);
        }

        return { type: 'queued' as const, operationKey };
      } catch (error: unknown) {
        const reconnectIssue = getConnectionReconnectRequiredIssue(error);
        if (reconnectIssue) {
          showWarnNotification({
            id: notifyId,
            title: 'Reconnect required',
            message: reconnectIssue.message,
          });
          openConnectionDetailDrawer(reconnectIssue.connectionId);
          return reconnectIssue;
        }

        console.error(error);
        showApiErrorNotification({
          error,
          title: 'Failed to refetch',
          defaultMessage:
            'Failed to refetch. Please reach out to us if the issue persists.',
        });
      }
    },
    [
      mutateAsync,
      notifyId,
      openConnectionDetailDrawer,
      queryClient,
      team,
      trackOperation,
    ]
  );

  return {
    mutate: extract,
    loading,
  };
};
